import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged } from 'type-fest';
import { type AggregationRoot, MutableEntity } from '../../common';
import type { CommandContext } from '../../common/context';

import Project = AuthDomain.Object.Project;

export type MetadataField = AggregationRoot &
  MutableEntity & {
    key: MetadataField.Key;
    name: string;
    dataType: MetadataField.DataType;
  };

export namespace MetadataField {
  export const DataType = {
    String: 'string',
    Number: 'number',
    Boolean: 'boolean',
  } as const;
  export type DataType = (typeof DataType)[keyof typeof DataType];

  export type Key = Tagged<string, 'MetadataFieldKey'>;

  export namespace Key {
    export function FromString(key: string): Key {
      if (key.length === 0) throw new Error('MetadataField key must not be empty');
      return key as Key;
    }
  }

  export type Props = MutableEntity.Props & {
    projectId: string;
    key: string;
    name: string;
    dataType: string;
  };
  export const Construct = (props: Props): MetadataField => {
    return {
      projectId: Project.Id.FromString(props.projectId),
      key: Key.FromString(props.key),
      name: props.name,
      dataType: props.dataType as DataType,
      ...MutableEntity.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Omit<MetadataField.Props, 'id' | 'projectId' | 'createdBy' | 'createdAt' | 'updatedBy' | 'updatedAt'>;
    export type Params = {
      context: CommandContext;
      props: Create.Props;
    };
  }
  export const Create = (params: Create.Params): MetadataField => {
    const { context, props } = params;
    const { projectId } = context;
    return {
      projectId,
      key: Key.FromString(props.key),
      name: props.name,
      dataType: props.dataType as DataType,
      ...MutableEntity.Create(context),
    };
  };

  export namespace Update {
    export type Params = {
      context: CommandContext;
      cur: MetadataField;
      props: Partial<Pick<MetadataField, 'name' | 'dataType'>>;
    };
  }
  export const Update = (params: Update.Params): MetadataField => {
    const { context, cur, props } = params;

    return {
      ...cur,
      name: props.name ?? cur.name,
      dataType: props.dataType ?? cur.dataType,
      ...MutableEntity.Update({ cur, ...context }),
    };
  };
}
